import React, { useEffect, useState } from 'react';
import { Eye, Wrench, Trash2 } from 'lucide-react';
import Toast, { ToastMessage } from '../../common/Toast';
import { confirmWarning } from '../../common/confirm';
import { capaRootCausesService, CapaRootCause } from '../../../services/capaRootCausesService';
import { Task, TaskStatus } from '../../../services/tasksService';
import extractApiError from '../../../utils/apiError';
import TaskDetailModal from './TaskDetailModal';

const taskStatusVariant = (s: TaskStatus): string => {
    switch (s) {
        case 'DONE':
        case 'VERIFIED':    return 'success';
        case 'IN_PROGRESS': return 'info';
        case 'REVERTED':
        case 'REJECTED':
        case 'CANCELLED':   return 'danger';
        case 'ARCHIVED':    return 'neutral';
        default:            return 'warning'; // OPEN
    }
};

interface Props {
    selectedCause: CapaRootCause | null;
    // Bumped by the surface after a new action is created so this column
    // reloads even though selectedCause reference hasn't changed.
    reloadKey?: number;
    onChanged: () => void;
    /** History/archive view — hide the unlink action; view-only. */
    readOnly?: boolean;
}

const ActionsColumn: React.FC<Props> = ({ selectedCause, reloadKey = 0, onChanged, readOnly = false }) => {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [detailTaskId, setDetailTaskId] = useState<number | null>(null);

    const loadTasks = async () => {
        if (!selectedCause) { setTasks([]); return; }
        try {
            const data = await capaRootCausesService.listTasks(selectedCause.id);
            setTasks(data);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load actions') });
        }
    };

    useEffect(() => { loadTasks(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedCause?.id, reloadKey]);

    if (!selectedCause) {
        return (
            <div className="capa-investigation-col">
                <div className="capa-investigation-col-header">
                    <Wrench size={14} />
                    Remedial Actions
                </div>
                <div className="capa-investigation-col-body">
                    <div className="capa-investigation-col-empty">
                        Select a cause on the RCA tree to view its action tasks. Open a cause to add remedial actions.
                    </div>
                </div>
            </div>
        );
    }

    const handleUnlink = async (task: Task) => {
        if (readOnly) return;
        const ok = await confirmWarning({
            title: 'Unlink this action from this cause?',
            text: 'The task itself remains, but it will no longer appear under this cause.',
            confirmText: 'Yes, Unlink',
        });
        if (!ok) return;
        try {
            await capaRootCausesService.unlinkTask(selectedCause.id, task.id);
            setToast({ type: 'success', message: 'Unlinked.' });
            loadTasks();
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to unlink') });
        }
    };

    return (
        <div className="capa-investigation-col">
            <div className="capa-investigation-col-header">
                <Wrench size={14} />
                Remedial Actions ({tasks.length})
            </div>
            <div className="capa-investigation-col-body">
                {tasks.length === 0 && (
                    <div className="capa-investigation-col-empty">
                        No remedial actions yet — open this cause to add one.
                    </div>
                )}
                {tasks.map(t => (
                    <div key={t.id} className="capa-action-row">
                        <span className="capa-action-row-text">{t.name}</span>
                        <span className={`status-badge sm ${taskStatusVariant(t.status)}`}>{t.status.replace('_', ' ')}</span>
                        <button
                            type="button"
                            className="btn-primary-outline-none"
                            onClick={() => setDetailTaskId(t.id)}
                            title="View / edit action"
                            data-testid={`BTN_actions_row_${t.id}_view`}
                        >
                            <Eye size={12} />
                        </button>
                        {!readOnly && (
                            <button
                                type="button"
                                onClick={() => handleUnlink(t)}
                                className="btn-danger-outline-none"
                                title="Unlink"
                                data-testid={`BTN_actions_row_${t.id}_unlink`}
                            >
                                <Trash2 size={12} />
                            </button>
                        )}
                    </div>
                ))}
            </div>
            <Toast toast={toast} onClose={() => setToast(null)} />
            <TaskDetailModal
                isOpen={!!detailTaskId}
                taskId={detailTaskId}
                frozen={readOnly}
                onClose={() => setDetailTaskId(null)}
                onChanged={() => { loadTasks(); onChanged(); }}
            />
        </div>
    );
};

export default ActionsColumn;
